import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, InputGroup } from 'react-bootstrap';
import moment from 'moment';
import PropTypes from 'prop-types';

import {
  requestList as request,
  requestInsertMixCompare
} from 'api/sfms/report';
import MonthPicker from 'components/common-sfims/form/MonthPicker';
import Select from 'components/common-sfims/form/Select';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText } from 'function/convert';
import { onInputChange } from 'function/event';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';

/**
 * 리포트 > 배합비 시행 계획 실적 비교 - 생성
 */
const Setting = ({ show, onHide, onSetting }) => {
  /* router */
  const { type } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { koreas: koreaPlants }
  } = usePlantState();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 중복 여부
  const [isDuplicate, setIsDuplicate] = useState(false);
  // 배합비 시행 계획 실적 비교 리포트 생성 요청 데이터
  const [reportData, setReportData] = useObjReducer({
    reportYm: moment().format('YYYYMM'),
    factoryCodeSeq: null
  });

  /* watch */
  useEffect(() => {
    // 중복 여부 설정
    setIsDuplicate(
      datas.some(
        ({ factoryCodeSeq, reportYm } = {}) =>
          factoryCodeSeq === reportData.factoryCodeSeq &&
          moment(reportYm).isSame(moment(reportData.reportYm), 'month')
      )
    );
  }, [datas]);
  useEffect(() => {
    // 필수 항목 입력 값이 유효한 경우 리포트 목록 조회 요청 (중복 여부 확인을 위함)
    if (show && isValidRequired(reportData)) {
      requestList();
    }
  }, [reportData]);

  /* method */
  // 리포트 목록 조회 요청
  const requestList = async () => {
    const { list = [] } = await request({
      ...reportData,
      reportType: type
    });

    setDatas(list);
  };

  /* event */
  // 설정
  const onSubmit = async event => {
    event.preventDefault();

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(reportData)) {
        throw { code: RESPONSE_CODE.BadRequestInsert };
      }

      const { code, message: error } = await requestInsertMixCompare(
        reportData
      );

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error
        };
      }

      // 설정 시 이벤트 실행
      onSetting();

      // 모달 숨김
      onHide();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        title: 'failure',
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <Modal show={show} onHide={onHide}>
      <Fragment key="header">{getText('insertReport')}</Fragment>
      <Fragment key="body">
        <Form onSubmit={onSubmit}>
          <Form.Group>
            <InputGroup className="mb-3">
              <InputGroup.Text className="w-25 fs--1">
                * {getText('selectYm')}
              </InputGroup.Text>
              <MonthPicker
                value={reportData.reportYm}
                placeholderText={getText('selectYm')}
                wrapperClassName="w-75"
                onUpdate={reportYm => setReportData({ reportYm })}
              />
            </InputGroup>
            <InputGroup className="mb-3">
              <InputGroup.Text className="w-25 fs--1">
                * {getText('selectFactory')}
              </InputGroup.Text>
              <Select
                datas={array2ValueAndText(koreaPlants, 'seq', 'pltNm')}
                placeholder="selectFactory"
                value={reportData.factoryCodeSeq}
                name="factoryCodeSeq"
                required
                onChange={event => onInputChange(event, setReportData)}
              />
            </InputGroup>
          </Form.Group>
          {isDuplicate && (
            <div className="d-flex flex-row align-items-center mb-3">
              <p className="fs--1 text-danger mb-0">
                * {getText('duplicateReport')}
              </p>
            </div>
          )}
          <div className="d-flex flex-row align-items-center justify-content-center">
            <Button variant="secondary" onClick={onHide}>
              {getText('cancel')}
            </Button>
            <Button disabled={isDisableSetting} type="submit" className="ms-2">
              {getText('insertReport')}
            </Button>
          </div>
        </Form>
      </Fragment>
    </Modal>
  );
};

Setting.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 설정 시 이벤트
  onSetting: PropTypes.func
};
Setting.defaultProps = { show: false };

export default Setting;
